import { z } from "zod";

/**
 * Sözleşme TEMEL BİLGİ kataloğu (CLOS-110 `[072]`).
 *
 * ### ⚠ Alan matrisi KONFİGÜRASYONDAN, koda gömülü değil
 *
 * Kartın açık kararı: *"kesin sözleşme metadata alanları ve sözleşme tür sınıflandırma
 * kataloğu konfigüre edilecektir"* ve *"yeni metadata alanları katalog üzerinden
 * versiyonlanır, KARTIN İÇİNE HARD-CODE EDİLMEZ."*
 *
 * ### Alan ↔ TÜR ilişkisi
 *
 * Kart: *"yalnız konfigüre edilmiş ... katalogda İLGİLİ TÜR İÇİN desteklenen alanlar
 * gösterilir ve doğrulanır."* Bir gizlilik sözleşmesinin parasal değeri yoktur; alan
 * herkese gösterilseydi kullanıcı anlamsız bir kutuyu doldurmaya çalışırdı.
 *
 * `types: null` = her türde geçerli. Boş dizi yerine `null`: "hiçbir türde" ile "her
 * türde" karışmasın.
 */
export const contractMetadataConfigSchema = z.object({
  types: z.array(z.object({ code: z.string().min(1), label: z.string().min(1) })).min(1),
  fields: z
    .array(
      z.object({
        key: z.string().min(1),
        /**
         * ⚠ KRİTİK alanda önceki ve yeni DEĞER audit'e giriyor (kart kriteri).
         *
         * `[050]` organizasyon profilinde tam tersi karar verilmişti: orada yalnız alan
         * ADLARI audit'e giriyor çünkü vergi numarası gibi resmî kimlikler kanıt kaydında
         * çoğaltılmamalı. Burada kart açıkça *"önceki ve yeni DEĞER korunur"* diyor —
         * sözleşme bedeli sonradan tartışılan bir şeydir ve "ne kadardı?" sorusunun
         * cevabı denetim kaydında olmalı.
         */
        critical: z.boolean(),
        /** `null` = her türde geçerli. */
        types: z.array(z.string().min(1)).nullable(),
      }),
    )
    .min(1),
  maxTags: z.number().int().positive(),
  tagMaxLength: z.number().int().positive(),
});
export type ContractMetadataConfig = z.infer<typeof contractMetadataConfigSchema>;
export type ContractTypeOption = ContractMetadataConfig["types"][number];
export type ContractFieldRule = ContractMetadataConfig["fields"][number];

/** Verilen türde DÜZENLENEBİLİR alanlar. Tür yoksa yalnız türden bağımsız alanlar. */
export function editableFieldsFor(
  config: ContractMetadataConfig,
  contractType: string | null,
): string[] {
  return config.fields
    .filter((f) => f.types === null || (contractType !== null && f.types.includes(contractType)))
    .map((f) => f.key);
}

/** Kritik alanlar — audit'e DEĞERİYLE giren küme. */
export function criticalFields(config: ContractMetadataConfig): string[] {
  return config.fields.filter((f) => f.critical).map((f) => f.key);
}

/**
 * Temel bilgi güncelleme girdisi.
 *
 * ⚠ `version` ZORUNLU: iki kullanıcı aynı sözleşmeyi aynı anda düzenlediğinde ikincisi
 * birincinin değişikliğini sessizce ezmesin (`[050]` ile aynı desen ve aynı gerekçe).
 *
 * Alanlar `nullish`: `null` "sildim", eksik olması "dokunmadım" demek. İkisi aynı şey
 * olsaydı bir alanı temizlemenin yolu kalmazdı.
 */
export const updateContractBasicsSchema = z.object({
  title: z.string().trim().min(1, "Sözleşme başlığı zorunludur").max(200).optional(),
  contractType: z.string().min(1).nullish(),
  description: z.string().max(4000, "Açıklama en fazla 4000 karakter olabilir").nullish(),
  referenceNumber: z
    .string()
    .max(100, "Referans numarası en fazla 100 karakter olabilir")
    .nullish(),
  /** ISO 4217: üç büyük harf. Serbest metin "TL" ile "TRY"yi yan yana getirirdi. */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/, "Para birimi üç harfli ISO kodu olmalıdır")
    .nullish(),
  /** Kuruş hassasiyeti metinle taşınıyor: kayan nokta parada güvenilmez. */
  value: z
    .string()
    .regex(/^\d{1,16}(\.\d{1,2})?$/, "Değer geçerli bir tutar olmalıdır")
    .nullish(),
  tags: z.array(z.string().trim().min(1)).nullish(),
  version: z.number({ error: "Sürüm bilgisi eksik" }).int().nonnegative(),
});
export type UpdateContractBasicsInput = z.infer<typeof updateContractBasicsSchema>;
